import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Track last sent page view globally to prevent duplicates across component mounts
let lastSentPageView = { path: '', timestamp: 0 };

/**
 * Hook to track page views in Google Analytics
 * Automatically tracks route changes in the SPA
 */
export function usePageTracking() {
  const location = useLocation();
  const hasTrackedInitial = useRef(false);

  useEffect(() => {
    // Check if gtag is available
    if (typeof window.gtag !== 'function') {
      return;
    }

    const currentPath = location.pathname;
    const now = Date.now();
    
    // Prevent duplicate tracking:
    // 1. Same path within 1 second (handles rapid remounts/rerenders)
    // 2. Skip if we've already tracked this on initial mount
    if (
      (lastSentPageView.path === currentPath && (now - lastSentPageView.timestamp) < 1000) ||
      (hasTrackedInitial.current && lastSentPageView.path === currentPath)
    ) {
      console.log('📊 GA4 Page View (skipped duplicate):', currentPath);
      return;
    }

    // Mark as tracked
    hasTrackedInitial.current = true;
    lastSentPageView = { path: currentPath, timestamp: now };

    // Get page title from route
    const pageTitle = getPageTitle(currentPath);
    
    // Update document title
    document.title = pageTitle;

    // Send page_view event to GA4
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: currentPath,
    });

    console.log('📊 GA4 Page View:', pageTitle, currentPath);
  }, [location.pathname]); // Only re-run when pathname changes
}

/**
 * Convert route paths to readable page titles
 */
function getPageTitle(pathname: string): string {
  // Remove leading slash and split by '/'
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length === 0) return 'Lureo - Home';
  
  // Handle specific route patterns
  // /story/edit/:storyId (legacy: /projects/:id/stories/:storyId/edit)
  if ((parts[0] === 'story' && parts[1] === 'edit') ||
      (parts[0] === 'projects' && parts[2] === 'stories' && parts[4] === 'edit')) {
    return 'Lureo - Story Edit';
  }

  // /project/edit/:id (legacy: /projects/:id/edit)
  if ((parts[0] === 'project' && parts[1] === 'edit') ||
      (parts[0] === 'projects' && parts[2] === 'edit')) {
    return 'Lureo - Project Edit';
  }

  // /project/:id (legacy: /projects/:id)
  if ((parts[0] === 'project' || parts[0] === 'projects') && parts.length === 2) {
    return 'Lureo - Project Detail';
  }
  
  // /editor/:sceneId
  if (parts[0] === 'editor' && parts.length === 2) {
    return 'Lureo - Editor';
  }
  
  // Simple route mapping for single-level routes
  const routeTitles: Record<string, string> = {
    'dashboard': 'Dashboard',
    'account': 'My Account',
    'my-account': 'My Account',
    'projects': 'Projects',
    'project': 'Project',
    'billing': 'Billing',
    'login': 'Login',
  };

  const firstPart = parts[0];
  const title = routeTitles[firstPart] || firstPart.charAt(0).toUpperCase() + firstPart.slice(1);
  
  return `Lureo - ${title}`;
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

